import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  IconButton,
  Breadcrumbs,
  Link,
  Divider,
  Alert,
  TextField,
  Card,
  CardContent,
  CardActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  NavigateNext as NavigateNextIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Article as ArticleIcon,
  QuestionAnswer as QuestionAnswerIcon,
} from "@mui/icons-material";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  fetchAssessment,
  updateAssessmentFormSchema,
} from "../../../redux/actions/assessmentActions";
import {
  FormOutputSchema,
  FormSection,
  FormQuestion,
} from "../../../redux/types/formTypes";
import { toast } from "react-toastify";
import HybridSchemaEditor from "./HybridSchemaEditor";
import JsonSchemaAnswerRenderer from "./JsonSchemaAnswerRenderer";

const AssessmentFormView: React.FC = () => {
  const { assessmentId, organizationId } = useParams<{
    assessmentId: string;
    organizationId: string;
  }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { currentAssessment, loading, error } = useSelector(
    (state: RootState) => state.assessments
  );

  const [formSchema, setFormSchema] = useState<FormOutputSchema>({
    sections: {},
    form_description: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    if (assessmentId && organizationId) {
      dispatch(fetchAssessment({ assessmentId, organizationId }));
    }
  }, [dispatch, assessmentId, organizationId]);

  useEffect(() => {
    if (currentAssessment?.form_output_schema) {
      setFormSchema(currentAssessment.form_output_schema);
      setIsEditing(false);
    } else if (currentAssessment && !currentAssessment.form_output_schema) {
      // If no schema exists, start in editing mode
      setIsEditing(true);
    }
  }, [currentAssessment]);

  const handleBackToAssessment = () => {
    if (isEditing && hasUnsavedChanges) {
      setCancelDialogOpen(true);
    } else {
      navigate(`/dashboard/assessments/${organizationId}/${assessmentId}`);
    }
  };

  const addSection = () => {
    const existingSectionCount = Object.keys(formSchema.sections).length;
    const newSectionId = `section_${existingSectionCount + 1}`;
    setFormSchema({
      ...formSchema,
      sections: {
        ...formSchema.sections,
        [newSectionId]: {
          section_id: newSectionId,
          section_title: "",
          section_description: "",
          questions: {},
        },
      },
    });
    
    // Scroll to the new section after a brief delay to allow rendering
    setTimeout(() => {
      const element = document.getElementById(`section-${newSectionId}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  const updateSection = (
    sectionId: string,
    field: keyof FormSection,
    value: string
  ) => {
    setFormSchema({
      ...formSchema,
      sections: {
        ...formSchema.sections,
        [sectionId]: {
          ...formSchema.sections[sectionId],
          [field]: value,
        },
      },
    });
  };

  const deleteSection = (sectionId: string) => {
    const { [sectionId]: _, ...remainingSections } = formSchema.sections;
    setFormSchema({
      ...formSchema,
      sections: remainingSections,
    });
  };

  const addQuestion = (sectionId: string) => {
    const currentQuestionCount = Object.keys(
      formSchema.sections[sectionId].questions
    ).length;
    const newQuestionId = `${sectionId}_question${currentQuestionCount + 1}`;
    setFormSchema({
      ...formSchema,
      sections: {
        ...formSchema.sections,
        [sectionId]: {
          ...formSchema.sections[sectionId],
          questions: {
            ...formSchema.sections[sectionId].questions,
            [newQuestionId]: {
              text: "",
              answer_json_schema: {},
            },
          },
        },
      },
    });
    
    // Scroll to the new question after a brief delay to allow rendering
    setTimeout(() => {
      const element = document.getElementById(`question-${newQuestionId}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  const updateQuestion = (
    sectionId: string,
    questionId: string,
    field: keyof FormQuestion,
    value: string | Record<string, any>
  ) => {
    setFormSchema({
      ...formSchema,
      sections: {
        ...formSchema.sections,
        [sectionId]: {
          ...formSchema.sections[sectionId],
          questions: {
            ...formSchema.sections[sectionId].questions,
            [questionId]: {
              ...formSchema.sections[sectionId].questions[questionId],
              [field]: value,
            },
          },
        },
      },
    });
    setHasUnsavedChanges(true);
  };

  const deleteQuestion = (sectionId: string, questionId: string) => {
    const { [questionId]: _, ...remainingQuestions } =
      formSchema.sections[sectionId].questions;
    setFormSchema({
      ...formSchema,
      sections: {
        ...formSchema.sections,
        [sectionId]: {
          ...formSchema.sections[sectionId],
          questions: remainingQuestions,
        },
      },
    });
  };

  const handleSave = async () => {
    if (!assessmentId || !organizationId) {
      toast.error("Missing assessment or organization ID");
      return;
    }

    if (!currentAssessment) {
      toast.error("Current assessment not found");
      return;
    }

    try {
      const result = await dispatch(
        updateAssessmentFormSchema({
          assessment_id: assessmentId,
          form_output_schema: formSchema,
          organization_id: organizationId,
        })
      ).unwrap();

      // Clear unsaved changes flag
      setHasUnsavedChanges(false);

      // The backend creates a new assessment version, so redirect to it
      if (result.id && result.id !== assessmentId) {
        toast.success(
          "Form schema saved successfully! Redirecting to new version..."
        );

        // Navigate to the new assessment version
        setTimeout(() => {
          navigate(
            `/dashboard/assessments/${result.organization_id}/${result.id}/form`,
            { replace: true }
          );
        }, 0);
      } else {
        toast.success("Form schema saved successfully!");
        setIsEditing(false);
        // Refresh the current assessment to get the latest data
        dispatch(fetchAssessment({ assessmentId, organizationId }));
      }
    } catch (err) {
      toast.error("Failed to save form schema");
      console.error("Error saving form schema:", err);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">Error: {error}</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/dashboard/assessments")}
          sx={{ mt: 2 }}
        >
          Back to Assessments
        </Button>
      </Box>
    );
  }

  if (!currentAssessment) {
    return (
      <Box p={3}>
        <Typography>Assessment not found</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/dashboard/assessments")}
          sx={{ mt: 2 }}
        >
          Back to Assessments
        </Button>
      </Box>
    );
  }

  // Check if processing type is not "form"
  if (currentAssessment.assessment_processing_type !== "form") {
    return (
      <Box p={3}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={handleBackToAssessment}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">Assessment Form</Typography>
        </Box>
        <Alert severity="warning">
          Cannot display form as processing type is set to "
          {currentAssessment.assessment_processing_type}".
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToAssessment}
          sx={{ mt: 2 }}
        >
          Back to Assessment
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Navigation */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={handleBackToAssessment}>
          <ArrowBackIcon />
        </IconButton>
        <Box flexGrow={1}>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <Link
              color="inherit"
              href="/dashboard/assessments"
              onClick={(e) => {
                e.preventDefault();
                navigate("/dashboard/assessments");
              }}
              sx={{ cursor: "pointer" }}
            >
              Assessments
            </Link>
            <Link
              color="inherit"
              onClick={(e) => {
                e.preventDefault();
                handleBackToAssessment();
              }}
              sx={{ cursor: "pointer" }}
            >
              {currentAssessment.display_title ||
                currentAssessment.scenario_description?.substring(0, 50) ||
                "Assessment"}
            </Link>
            <Typography color="text.primary">Form Builder</Typography>
          </Breadcrumbs>
          <Typography variant="h4" sx={{ mt: 1 }}>
            Assessment Form Builder
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          {!isEditing ? (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setIsEditing(true)}
            >
              Edit Form
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                onClick={() => setCancelDialogOpen(true)}
              >
                Cancel
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Form Content */}
      <Paper sx={{ p: 4 }}>
        {!isEditing ? (
          /* Preview Mode - Show form as it will appear to users */
          <>
            {formSchema.form_description && (
              <Box mb={3}>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ fontStyle: "italic" }}
                >
                  {formSchema.form_description}
                </Typography>
              </Box>
            )}

            {Object.keys(formSchema.sections).length === 0 ? (
              <Alert severity="info">
                No sections added yet. Switch to edit mode to start building
                your form.
              </Alert>
            ) : (
              Object.entries(formSchema.sections).map(
                ([sectionId, section], sectionIndex) => (
                  <Accordion
                    key={sectionId}
                    defaultExpanded={true}
                    sx={{ mb: 2 }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        bgcolor: "primary.50",
                        "&:hover": { bgcolor: "primary.100" },
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <ArticleIcon color="primary" />
                        <Typography variant="h6" fontWeight="medium">
                          {section.section_title || "Untitled Section"}
                        </Typography>
                        <Chip
                          label={`Section ${sectionIndex + 1}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        {section.section_description && (
                          <Box mb={3}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontStyle: "italic" }}
                            >
                              {section.section_description}
                            </Typography>
                          </Box>
                        )}

                        {Object.keys(section.questions).length === 0 ? (
                          <Alert severity="info">
                            No questions in this section.
                          </Alert>
                        ) : (
                          Object.entries(section.questions).map(
                            ([questionId, question], qIndex) => (
                              <Box key={questionId} mb={4}>
                                {/* Question Header */}
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={2}
                                  mb={1}
                                >
                                  <QuestionAnswerIcon
                                    fontSize="small"
                                    color="action"
                                  />
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight="medium"
                                  >
                                    Question {qIndex + 1}
                                  </Typography>
                                </Box>

                                {/* Question Text */}
                                <Typography
                                  variant="body1"
                                  sx={{ mb: 2, pl: 3.5 }}
                                >
                                  {question.text || "No question text"}
                                </Typography>

                                {/* Answer Field Preview */}
                                <Box pl={3.5}>
                                  <Typography
                                    variant="subtitle2"
                                    fontWeight="medium"
                                    gutterBottom
                                    color="text.secondary"
                                  >
                                    Answer
                                  </Typography>
                                  {question.answer_json_schema &&
                                  Object.keys(question.answer_json_schema)
                                    .length > 0 ? (
                                    <JsonSchemaAnswerRenderer
                                      schema={question.answer_json_schema}
                                      readonly={true}
                                      showAnswerStatus={false}
                                    />
                                  ) : (
                                    <Alert severity="warning" sx={{ mt: 1 }}>
                                      No answer schema configured
                                    </Alert>
                                  )}
                                </Box>

                                {qIndex <
                                  Object.keys(section.questions).length - 1 && (
                                  <Divider sx={{ mt: 3 }} />
                                )}
                              </Box>
                            )
                          )
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )
              )
            )}
          </>
        ) : (
          /* Edit Mode - Show form builder */
          <>
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>
                Form Description
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                value={formSchema.form_description || ""}
                onChange={(e) =>
                  setFormSchema({
                    ...formSchema,
                    form_description: e.target.value,
                  })
                }
                placeholder="Enter form description..."
                disabled={!isEditing}
                sx={{ mb: 3 }}
              />
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6">Sections</Typography>
              {isEditing && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addSection}
                >
                  Add Section
                </Button>
              )}
            </Box>

            {Object.keys(formSchema.sections).length === 0 ? (
              <Alert severity="info">
                No sections added yet. Click "Start Editing" then "Add Section"
                to get started.
              </Alert>
            ) : (
              Object.entries(formSchema.sections).map(
                ([sectionId, section]) => (
                  <Accordion key={sectionId} defaultExpanded sx={{ mb: 2 }} id={`section-${sectionId}`}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle1">
                        {section.section_title || "Untitled Section"}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        <TextField
                          fullWidth
                          label="Section Title"
                          value={section.section_title}
                          onChange={(e) =>
                            updateSection(
                              sectionId,
                              "section_title",
                              e.target.value
                            )
                          }
                          disabled={!isEditing}
                          sx={{ mb: 2 }}
                        />
                        <TextField
                          fullWidth
                          label="Section Description"
                          multiline
                          rows={2}
                          value={section.section_description || ""}
                          onChange={(e) =>
                            updateSection(
                              sectionId,
                              "section_description",
                              e.target.value
                            )
                          }
                          disabled={!isEditing}
                          sx={{ mb: 3 }}
                        />

                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={2}
                        >
                          <Typography variant="subtitle2">Questions</Typography>
                          {isEditing && (
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => addQuestion(sectionId)}
                            >
                              Add Question
                            </Button>
                          )}
                        </Box>

                        {Object.keys(section.questions).length === 0 ? (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            No questions in this section.
                          </Alert>
                        ) : (
                          Object.entries(section.questions).map(
                            ([questionId, question]) => (
                              <Card
                                key={questionId}
                                variant="outlined"
                                sx={{ mb: 2 }}
                                id={`question-${questionId}`}
                              >
                                <CardContent>
                                  <TextField
                                    fullWidth
                                    label="Question Text"
                                    value={question.text}
                                    onChange={(e) =>
                                      updateQuestion(
                                        sectionId,
                                        questionId,
                                        "text",
                                        e.target.value
                                      )
                                    }
                                    disabled={!isEditing}
                                    sx={{ mb: 3 }}
                                  />
                                  <Divider sx={{ mb: 3 }} />
                                  <Typography
                                    variant="subtitle1"
                                    gutterBottom
                                    fontWeight="bold"
                                  >
                                    Answer Schema
                                  </Typography>
                                  <HybridSchemaEditor
                                    value={question.answer_json_schema || {}}
                                    onChange={(schema) =>
                                      updateQuestion(
                                        sectionId,
                                        questionId,
                                        "answer_json_schema",
                                        schema
                                      )
                                    }
                                    disabled={!isEditing}
                                  />
                                </CardContent>
                                {isEditing && (
                                  <CardActions>
                                    <Button
                                      size="small"
                                      color="error"
                                      startIcon={<DeleteIcon />}
                                      onClick={() =>
                                        deleteQuestion(sectionId, questionId)
                                      }
                                    >
                                      Delete Question
                                    </Button>
                                  </CardActions>
                                )}
                              </Card>
                            )
                          )
                        )}

                        {isEditing && (
                          <Box display="flex" justifyContent="flex-end" mt={2}>
                            <Button
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() => deleteSection(sectionId)}
                            >
                              Delete Section
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )
              )
            )}
          </>
        )}
      </Paper>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
      >
        <DialogTitle>Discard Changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel? All unsaved changes will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>
            Continue Editing
          </Button>
          <Button
            onClick={() => {
              if (currentAssessment.form_output_schema) {
                setFormSchema(currentAssessment.form_output_schema);
              }
              setIsEditing(false);
              setHasUnsavedChanges(false);
              setCancelDialogOpen(false);
              // If this was triggered by back button, navigate back
              if (window.history.state?.usr?.fromBack) {
                navigate(`/dashboard/assessments/${organizationId}/${assessmentId}`);
              }
            }}
            color="error"
            variant="contained"
          >
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssessmentFormView;
